import { useEffect, useState } from "react";
import { Loader2, UserCheck, Wand2 } from "lucide-react";
import { api } from "../../lib/api";

/** V183 — Looks up an Enquiry / Student ID and offers a one-tap "Fill details". */
export default function LeadAutofill({ refValue, onFill }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dismissed, setDismissed] = useState([]);
    const ref = (refValue || "").trim();

    useEffect(() => {
        setItems([]);
        if (ref.length < 4) return undefined;
        let alive = true;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api.get("/admin/payment/lead-lookup", { params: { ref } });
                if (alive) setItems(res.data.items || []);
            } catch {
                if (alive) setItems([]);
            } finally {
                if (alive) setLoading(false);
            }
        }, 500);
        return () => { alive = false; clearTimeout(t); };
    }, [ref]);

    if (ref.length < 4 || dismissed.includes(ref)) return null;
    if (loading) return <div className="mt-1.5 text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid="lead-autofill-loading"><Loader2 className="w-3 h-3 animate-spin" /> Looking up this ID…</div>;
    if (!items.length) return <div className="mt-1.5 text-[11px] text-slate-400" data-testid="lead-autofill-none">No saved lead or earlier payment link for this ID.</div>;

    return (
        <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50/60 p-2.5 space-y-2" data-testid="lead-autofill">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E3A8A] inline-flex items-center gap-1">
                <UserCheck className="w-3.5 h-3.5" /> {items.length === 1 ? "Match found" : `${items.length} matches — pick one`}
            </div>
            {items.map((m, i) => (
                <div key={`${m.source}-${m.lead_id || i}`} className="flex flex-wrap items-center gap-2 bg-white rounded-md border border-slate-200 px-2.5 py-2" data-testid={`lead-autofill-item-${i}`}>
                    <div className="flex-1 min-w-0 text-xs text-slate-700 break-words">
                        <span className="font-semibold text-[#0F172A]">{[m.student_name, m.parent_name].filter(Boolean).join(" · ") || m.client_search_name || "Unnamed"}</span>
                        {m.contact_number && <span className="text-emerald-700"> · +{m.contact_number}</span>}
                        <div className="text-[10px] text-slate-500">{m.source_label}{m.lead_id ? ` · …${m.lead_id.slice(-4).toUpperCase()}` : ""}{m.created_at ? ` · ${new Date(m.created_at).toLocaleDateString("en-IN")}` : ""}</div>
                    </div>
                    <button type="button" onClick={() => { onFill(m); setDismissed((d) => [...d, ref, m.enquiry_id]); }} data-testid={`lead-autofill-fill-${i}`}
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#1E3A8A] text-white text-[11px] font-bold hover:bg-[#1E40AF]">
                        <Wand2 className="w-3.5 h-3.5" /> Fill details
                    </button>
                </div>
            ))}
            <button type="button" onClick={() => setDismissed((d) => [...d, ref])} className="text-[10px] text-slate-500 hover:text-slate-700 underline" data-testid="lead-autofill-dismiss">Dismiss</button>
        </div>
    );
}
